import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { gestApi } from "./GestAuthContext";
import { motion, AnimatePresence } from "framer-motion";
import SpeseLibereFlow from "./documenti/SpeseLibereFlow";
import IncassoFlow from "./documenti/IncassoFlow";
import { money, CATEGORIE, TIPI_PRODOTTO } from "./documenti/constants";



const DOC_TYPES = [
  {
    key: "fattura",
    title: "Fattura fornitore",
    sub: "Costo verso il cantiere",
    desc: "PDF, immagine o Excel di una fattura ricevuta. L'IA estrae fornitore, data, importo e categoria.",
    fields: ["fornitore", "data", "numero_documento", "importo_totale", "categoria", "cantiere_id", "note"],
    color: "#3b82f6",
  },
  {
    key: "ddt_in",
    title: "DDT in ingresso",
    sub: "Materia prima ricevuta",
    desc: "DDT di materiale ricevuto (profili, vetro, ferramenta, accessori). Crea un costo e collega il materiale.",
    fields: ["fornitore", "data", "numero_documento", "materiale", "quantita", "unita", "importo_totale", "categoria", "cantiere_id", "note"],
    color: "#8b5cf6",
  },
  {
    key: "ddt_out",
    title: "DDT in uscita",
    sub: "Prodotto finito → cantiere",
    desc: "DDT di prodotto finito spedito al cantiere. Aggiorna lo stato «materiale consegnato».",
    fields: ["data", "numero_documento", "tipo_prodotto", "quantita", "cantiere_id", "note"],
    color: "#16a34a",
  },
  {
    key: "spesa_libera",
    title: "Spesa libera",
    sub: "Testo IA o Excel senza fattura",
    desc: "Scrivi in linguaggio naturale (es. «oggi per Genova ho speso 50 di sopralluogo») o carica un Excel con più righe.",
    fields: [],
    color: "#f59e0b",
  },
  {
    key: "incasso",
    title: "Fattura & Incasso",
    sub: "Importo emesso e ricevuto",
    desc: "Registra una fattura emessa al cliente e l'incasso ricevuto. Traccia quanto manca da riscuotere per ogni cantiere.",
    fields: [],
    color: "#0d9488",
  },
];


const FIELD_LABEL = {
  fornitore: "Fornitore",
  data: "Data documento",
  numero_documento: "N° documento",
  importo_totale: "Importo totale (€)",
  categoria: "Categoria costo",
  materiale: "Descrizione materiale",
  quantita: "Quantità",
  unita: "Unità di misura",
  tipo_prodotto: "Tipo prodotto",
  cantiere_id: "Cantiere",
  note: "Note",
};

const REQUIRED = {
  fattura: ["fornitore", "data", "importo_totale", "categoria", "cantiere_id"],
  ddt_in: ["fornitore", "data", "importo_totale", "categoria", "cantiere_id"],
  ddt_out: ["data", "tipo_prodotto", "quantita", "cantiere_id"],
};

function Field({ name, value, onChange, cantieri, required, wasMissing }) {
  const label = FIELD_LABEL[name] || name;
  const showWarning = wasMissing && !value;
  const baseInput = `w-full border rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:border-[#0a1420] ${showWarning ? "border-orange-400 bg-orange-50" : "border-black/10"}`;
  const req = required.includes(name);

  const wrap = (children) => (
    <div>
      <label className="block text-[12px] mb-1">
        <span className={showWarning ? "text-orange-600 font-medium" : "text-[#6e6e73]"}>{label}{req && " *"}</span>
        {showWarning && <span className="ml-2 text-[11px] text-orange-500">IA non è riuscita a estrarre — verifica manualmente</span>}
      </label>
      {children}
    </div>
  );

  if (name === "categoria") {
    return wrap(
      <select value={value || ""} onChange={(e) => onChange(name, e.target.value || null)} className={`${baseInput} bg-white`} data-testid={`gest-doc-field-${name}`}>
        <option value="">— Seleziona —</option>
        {CATEGORIE.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
    );
  }
  if (name === "tipo_prodotto") {
    return wrap(
      <select value={value || ""} onChange={(e) => onChange(name, e.target.value || null)} className={`${baseInput} bg-white`} data-testid={`gest-doc-field-${name}`}>
        <option value="">— Seleziona —</option>
        {TIPI_PRODOTTO.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
    );
  }
  if (name === "cantiere_id") {
    return wrap(
      <select value={value || ""} onChange={(e) => onChange(name, e.target.value || null)} className={`${baseInput} bg-white`} data-testid={`gest-doc-field-${name}`}>
        <option value="">— Seleziona cantiere —</option>
        {cantieri.map((c) => <option key={c.id} value={c.id}>{c.nome} · {c.cliente}</option>)}
      </select>
    );
  }
  if (name === "data") {
    return wrap(
      <input type="date" value={value || ""} onChange={(e) => onChange(name, e.target.value || null)} className={baseInput} data-testid={`gest-doc-field-${name}`} />
    );
  }
  if (name === "importo_totale" || name === "quantita") {
    return wrap(
      <input type="number" step="0.01" value={value ?? ""} onChange={(e) => onChange(name, e.target.value === "" ? null : Number(e.target.value))} className={baseInput} data-testid={`gest-doc-field-${name}`} />
    );
  }
  if (name === "note") {
    return wrap(
      <textarea rows={2} value={value || ""} onChange={(e) => onChange(name, e.target.value)} className={`${baseInput} resize-none`} data-testid={`gest-doc-field-${name}`} />
    );
  }
  return wrap(
    <input type="text" value={value || ""} onChange={(e) => onChange(name, e.target.value || null)} className={baseInput} data-testid={`gest-doc-field-${name}`} />
  );
}

export default function GestDocumenti() {
  const [step, setStep] = useState(1); // 1=pick+upload, 2=review, 3=success
  const [docType, setDocType] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [cantieri, setCantieri] = useState([]);
  const [missing, setMissing] = useState([]); // fields the AI couldn't extract
  const [savedInfo, setSavedInfo] = useState(null);
  const inputRef = useRef(null);

  // History (last 15 documents)
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const { data } = await gestApi.get("/documents");
      setHistory(data);
    } catch (e) { console.error("[gest:documenti] history load failed:", e); }
    finally { setLoadingHistory(false); }
  };

  useEffect(() => { loadHistory(); }, []);

  const cfg = DOC_TYPES.find((d) => d.key === docType);
  const required = docType ? REQUIRED[docType] : [];

  const reset = () => {
    setStep(1); setDocType(null); setFile(null); setExtracted(null);
    setFileId(null); setCantieri([]); setMissing([]); setSavedInfo(null);
  };

  const onFilePicked = (f) => {
    if (!f) return;
    const okTypes = [
      "application/pdf",
      "image/png", "image/jpeg", "image/jpg", "image/webp",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    const okExt = /\.(pdf|png|jpe?g|webp|xlsx|xls)$/i.test(f.name);
    if (!okTypes.includes(f.type) && !okExt) {
      toast.error("Formato non supportato. Usa PDF, immagine o Excel.");
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      toast.error("File troppo grande (max 15 MB).");
      return;
    }
    setFile(f);
  };

  const runExtraction = async () => {
    if (!file || !docType) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    try {
      const { data } = await gestApi.post("/documents/extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const filled = data.extracted || {};
      const miss = (cfg?.fields || []).filter((k) => k !== "note" && (filled[k] === null || filled[k] === undefined || filled[k] === ""));
      setExtracted(filled);
      setFileId(data.file_id);
      setCantieri(data.cantieri || []);
      setMissing(miss);
      setStep(2);
      if (data.error) {
        toast.warning(`IA non ha potuto estrarre alcuni campi — compilali manualmente.`);
      } else if (miss.length === 0) {
        toast.success("Tutti i campi estratti — verifica e conferma.");
      } else {
        toast.info(`${miss.length} campo/i da verificare manualmente.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Estrazione fallita");
    } finally { setBusy(false); }
  };

  const updateField = (name, value) => {
    setExtracted((s) => ({ ...s, [name]: value }));
    if (value !== null && value !== "" && missing.includes(name)) {
      setMissing((m) => m.filter((k) => k !== name));
    }
  };

  const confirm = async () => {
    // Client-side validation
    const missingReq = required.filter((k) => extracted[k] === null || extracted[k] === undefined || extracted[k] === "");
    if (missingReq.length) {
      toast.error("Compila i campi obbligatori: " + missingReq.map((k) => FIELD_LABEL[k]).join(", "));
      return;
    }
    setBusy(true);
    try {
      const { data } = await gestApi.post("/documents/confirm", {
        doc_type: docType,
        file_id: fileId,
        data: extracted,
      });
      setSavedInfo(data);
      setStep(3);
      toast.success("Documento salvato e cantiere aggiornato.");
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-5 md:p-8 max-w-[1000px] mx-auto" data-testid="gest-documenti-page">
      <div className="mb-6">
        <div className="text-[12px] uppercase tracking-widest text-[#6e6e73]">Documenti IA</div>
        <h1 className="text-[26px] md:text-[34px] font-semibold tracking-[-0.02em] text-[#0a1420]">Lettura automatica documenti</h1>
        <div className="text-[13px] text-[#6e6e73] mt-1">Carica una fattura o un DDT. L'IA estrae i dati, tu confermi e il cantiere si aggiorna.</div>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-3 mb-6 text-[12px]">
        {["1. Tipo & Upload", "2. Revisione", "3. Confermato"].map((lbl, i) => {
          const idx = i + 1;
          const active = step === idx;
          const done = step > idx;
          return (
            <div key={lbl} className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded-full text-[11px] font-semibold flex items-center justify-center ${done ? "bg-[#0a1420] text-white" : active ? "bg-[#a7c8e9] text-[#0a1420]" : "bg-white text-[#6e6e73] border border-black/10"}`}>{done ? "✓" : idx}</div>
              <div className={active ? "text-[#0a1420] font-medium" : done ? "text-[#0a1420]" : "text-[#6e6e73]"}>{lbl}</div>
              {i < 2 && <div className="w-8 h-px bg-black/10 ml-1" />}
            </div>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        {/* STEP 1 — pick type + upload */}
        {step === 1 && (
          <motion.div key="s1" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="gest-doc-types">
              {DOC_TYPES.map((d) => {
                const selected = docType === d.key;
                return (
                  <button
                    key={d.key}
                    type="button"
                    onClick={() => setDocType(d.key)}
                    data-testid={`gest-doc-type-${d.key}`}
                    className={`text-left p-5 rounded-2xl transition-all border-2 ${selected ? "border-[#0a1420] bg-white shadow-md" : "border-black/[0.04] bg-white hover:border-black/20"}`}
                  >
                    <div className="w-8 h-8 rounded-lg mb-3" style={{ backgroundColor: d.color + "22", border: `1px solid ${d.color}55` }}>
                      <div className="w-full h-full rounded-lg" style={{ backgroundColor: d.color, opacity: selected ? 1 : 0.55 }} />
                    </div>
                    <div className="text-[15px] font-semibold text-[#0a1420]">{d.title}</div>
                    <div className="text-[12px] text-[#6e6e73] mt-0.5 mb-2">{d.sub}</div>
                    <div className="text-[12px] text-[#0a1420]/70 leading-relaxed">{d.desc}</div>
                  </button>
                );
              })}
            </div>

            {docType && docType !== "spesa_libera" && docType !== "incasso" && (
              <div className="bg-white rounded-2xl p-6 border border-black/[0.04] shadow-sm" data-testid="gest-doc-upload-box">
                <div className="text-[13px] font-semibold text-[#0a1420] mb-2">Carica il documento</div>
                <div className="text-[12px] text-[#6e6e73] mb-4">Formati accettati: PDF, immagine (JPG/PNG/WebP), Excel (XLSX). Max 15 MB.</div>

                <label
                  className={`block border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${file ? "border-[#0a1420] bg-[#f5f7fb]" : "border-black/10 hover:border-black/30 hover:bg-[#f5f7fb]"}`}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) onFilePicked(f); }}
                >
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,application/pdf,image/*,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={(e) => onFilePicked(e.target.files?.[0])}
                    data-testid="gest-doc-file-input"
                    className="sr-only"
                  />
                  {file ? (
                    <div>
                      <div className="text-[14px] text-[#0a1420] font-medium">{file.name}</div>
                      <div className="text-[12px] text-[#6e6e73] mt-1">{(file.size / 1024).toFixed(0)} KB · {file.type || "file"}</div>
                      <div className="text-[12px] text-[#0a1420] mt-2 underline">Cambia file</div>
                    </div>
                  ) : (
                    <div>
                      <div className="text-[14px] text-[#0a1420]">Trascina il file qui o clicca per selezionare</div>
                      <div className="text-[11px] text-[#6e6e73] mt-1">PDF · JPG · PNG · WebP · XLSX</div>
                    </div>
                  )}
                </label>

                <div className="mt-5 flex items-center justify-between gap-3">
                  <div className="text-[11px] text-[#6e6e73]">L'IA <span className="font-medium">Gemini 2.5 Flash</span> analizzerà il documento.</div>
                  <button
                    type="button"
                    disabled={!file || !docType || busy}
                    onClick={runExtraction}
                    data-testid="gest-doc-extract"
                    className="px-5 py-2.5 bg-[#0a1420] text-white rounded-lg text-[13px] font-semibold hover:bg-[#1a2432] disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {busy ? (<span className="inline-flex items-center gap-2"><span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Analisi in corso…</span>) : "Analizza con IA →"}
                  </button>
                </div>
              </div>
            )}

            {docType === "spesa_libera" && (
              <SpeseLibereFlow onSaved={() => { loadHistory(); }} />
            )}

            {docType === "incasso" && (
              <IncassoFlow onSaved={() => { loadHistory(); }} />
            )}

          </motion.div>
        )}

        {/* STEP 2 — review + edit */}
        {step === 2 && extracted && (
          <motion.div key="s2" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
            <div className="bg-white rounded-2xl p-6 border border-black/[0.04] shadow-sm" data-testid="gest-doc-review">
              <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                <div className="text-[15px] font-semibold text-[#0a1420]">Verifica dati estratti — {cfg?.title}</div>
                <button type="button" onClick={reset} className="text-[12px] text-[#6e6e73] hover:text-[#0a1420]">↺ Ricomincia</button>
              </div>
              <div className="text-[12px] text-[#6e6e73] mb-5">
                Controlla e correggi i campi. Niente viene scritto nel database finché non premi <b>Salva</b>.
                {missing.length > 0 && <span className="text-orange-600"> {missing.length} campo/i evidenziati in arancione — l'IA non è stata sicura.</span>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {(cfg?.fields || []).map((f) => (
                  <div key={f} className={f === "note" ? "md:col-span-2" : ""}>
                    <Field
                      name={f}
                      value={extracted[f]}
                      onChange={updateField}
                      cantieri={cantieri}
                      required={required}
                      wasMissing={missing.includes(f)}
                    />
                  </div>
                ))}
              </div>

              {extracted.cantiere_hint && !extracted.cantiere_id && (
                <div className="mb-4 text-[12px] p-3 rounded-lg bg-orange-50 border border-orange-100 text-orange-700">
                  Cantiere suggerito dall'IA: <b>{extracted.cantiere_hint}</b> — nessun cantiere corrispondente. Selezionane uno dalla lista.
                </div>
              )}

              <div className="flex items-center justify-between gap-3 pt-4 border-t border-black/5">
                <button type="button" onClick={() => setStep(1)} className="text-[13px] text-[#6e6e73] hover:text-[#0a1420]">‹ Cambia file</button>
                <button
                  type="button"
                  onClick={confirm}
                  disabled={busy}
                  data-testid="gest-doc-confirm"
                  className="px-5 py-2.5 bg-[#0a1420] text-white rounded-lg text-[13px] font-semibold hover:bg-[#1a2432] disabled:opacity-40"
                >
                  {busy ? "Salvataggio…" : "Salva e aggiorna cantiere"}
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* STEP 3 — success */}
        {step === 3 && savedInfo && (
          <motion.div key="s3" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
            <div className="bg-white rounded-2xl p-8 border border-black/[0.04] shadow-sm text-center" data-testid="gest-doc-success">
              <div className="w-14 h-14 rounded-full bg-[#16a34a] mx-auto flex items-center justify-center mb-4">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              </div>
              <div className="text-[18px] font-semibold text-[#0a1420]">Documento salvato ✓</div>
              <div className="text-[13px] text-[#6e6e73] mt-1 mb-5">
                {docType === "ddt_out"
                  ? `Consegna registrata: ${extracted.quantita} ${extracted.tipo_prodotto} arrivati al cantiere.`
                  : `Costo di ${money(extracted.importo_totale)} registrato sul cantiere. Margine ricalcolato.`}
              </div>
              <div className="flex items-center justify-center gap-3">
                <button onClick={reset} data-testid="gest-doc-new" className="px-5 py-2.5 bg-[#0a1420] text-white rounded-lg text-[13px] font-semibold hover:bg-[#1a2432]">Carica un altro documento</button>
                <a href={`/gestione/cantieri/${extracted.cantiere_id}`} className="px-5 py-2.5 border border-black/10 rounded-lg text-[13px] text-[#0a1420] hover:bg-[#f5f7fb]">Vedi cantiere</a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* History */}
      <div className="mt-8 bg-white rounded-2xl border border-black/[0.04] shadow-sm overflow-hidden" data-testid="gest-doc-history">
        <div className="px-5 py-3 border-b border-black/5 text-[13px] font-semibold text-[#0a1420]">Documenti recenti</div>
        {loadingHistory ? (
          <div className="p-6 text-center text-[13px] text-[#6e6e73]">Caricamento…</div>
        ) : history.length === 0 ? (
          <div className="p-6 text-center text-[13px] text-[#6e6e73]">Nessun documento salvato.</div>
        ) : (
          <div className="divide-y divide-black/5 max-h-[400px] overflow-y-auto">
            {history.map((d) => {
              const cfg = DOC_TYPES.find((x) => x.key === d.doc_type);
              const p = d.extracted_data || {};
              const isSpese = d.doc_type === "spesa_libera";
              const totSpese = isSpese && Array.isArray(p.items) ? p.items.reduce((s, it) => s + Number(it.importo || 0), 0) : 0;
              return (
                <div key={d.id} className="px-5 py-3 flex items-center gap-4">
                  <span className="w-2 h-8 rounded-full shrink-0" style={{ backgroundColor: cfg?.color || "#6e6e73" }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] text-[#0a1420] truncate font-medium">
                      {cfg?.title || d.doc_type} · {isSpese ? `${p.items?.length || 0} voci` : (p.fornitore || p.tipo_prodotto || "—")}
                    </div>
                    <div className="text-[11px] text-[#6e6e73] truncate">
                      {p.numero_documento && <>#{p.numero_documento} · </>}
                      {p.data && <>{p.data} · </>}
                      {isSpese ? money(totSpese) : (p.importo_totale ? money(p.importo_totale) : "")}
                      {p.quantita ? `${p.quantita} pezzi` : ""}
                    </div>
                  </div>
                  <div className="text-[11px] text-[#6e6e73] shrink-0">{new Date(d.created_at).toLocaleString("it-IT")}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------------
// Free-form expenses flow (text OR excel → AI parse → editable list → confirm)
// Rendered inline inside GestDocumenti when the user picks the "Spesa libera" card.
// -----------------------------------------------------------------------------
